/**
 * StudyDoc print letterhead — logo + "Made with {org} Twins".
 *
 * Cache scope: one-shot, not cached. Resolution mirrors Sidebar wordmark
 * rules so print and the app chrome cannot drift.
 */

export const FAIRGEN_PRINT_WORDMARK_SRC = '/fairgen-logo-light.svg'
export const DEFAULT_STUDY_DOC_PRINT_ORG_NAME = 'Fairgen'

export interface StudyDocPrintBrandSource {
  whiteLabel: boolean
  isDark: boolean
  orgName?: string | null
  organizationName?: string | null
  companyName?: string | null
  brandLogoUrl?: string | null
  brandLogoUrlOnDark?: string | null
}

export interface StudyDocPrintBrandResolved {
  logoSrc: string | null
  logoAlt: string
  orgName: string
  credit: string
  hasLightLogo: boolean
  hasDarkLogo: boolean
  hasDarkLogoOnly: boolean
  isUsingOrgWordmark: boolean
  invertProbeSrc: string | null
}

export function resolveStudyDocPrintOrgName(
  source: Pick<
    StudyDocPrintBrandSource,
    'orgName' | 'organizationName' | 'companyName'
  >,
): string {
  return (
    source.orgName?.trim() ||
    source.organizationName?.trim() ||
    source.companyName?.trim() ||
    DEFAULT_STUDY_DOC_PRINT_ORG_NAME
  )
}

export function formatStudyDocPrintCredit(orgName: string): string {
  const trimmed = orgName.trim() || DEFAULT_STUDY_DOC_PRINT_ORG_NAME

  if (trimmed === DEFAULT_STUDY_DOC_PRINT_ORG_NAME) {
    return `Made with ${trimmed}`
  }

  return `Made with ${trimmed} Twins`
}

export function resolveStudyDocPrintBrand(
  source: StudyDocPrintBrandSource,
): StudyDocPrintBrandResolved {
  const orgName = resolveStudyDocPrintOrgName(source)
  const light = source.brandLogoUrl ?? null
  const dark = source.brandLogoUrlOnDark ?? null
  const hasLightLogo = Boolean(light)
  const hasDarkLogo = Boolean(dark)
  const hasDarkLogoOnly = !hasLightLogo && hasDarkLogo
  const resolvedWordmarkUrl = source.whiteLabel
    ? (source.isDark ? dark || light : light || dark) || null
    : null
  const logoSrc =
    resolvedWordmarkUrl || (source.whiteLabel ? null : FAIRGEN_PRINT_WORDMARK_SRC)
  const isUsingOrgWordmark =
    source.whiteLabel &&
    logoSrc !== null &&
    logoSrc !== FAIRGEN_PRINT_WORDMARK_SRC
  const invertProbeSrc = hasDarkLogoOnly ? dark : dark ? null : light

  return {
    logoSrc,
    logoAlt: orgName,
    orgName,
    credit: formatStudyDocPrintCredit(orgName),
    hasLightLogo,
    hasDarkLogo,
    hasDarkLogoOnly,
    isUsingOrgWordmark,
    invertProbeSrc,
  }
}

export function resolveStudyDocPrintLogoInvert(args: {
  isUsingOrgWordmark: boolean
  isDark: boolean
  hasDarkLogo: boolean
  hasDarkLogoOnly: boolean
  logoNeedsInversion: boolean
}): boolean {
  if (args.isUsingOrgWordmark) {
    return (
      (args.isDark && !args.hasDarkLogo && args.logoNeedsInversion) ||
      (!args.isDark && args.hasDarkLogoOnly && args.logoNeedsInversion)
    )
  }

  return args.isDark
}
